import React from "react";

import {
  bucketEvents,
  buildCourseIndex,
  eventCourseSummary,
  formatEventDate,
  isPastClubCalendarEvent,
  normalizeEvent,
  parseEventDate,
  statusLabel,
  typeLabel,
} from "../shared/events-model.js";
import { feedDateInfo } from "../shared/home-feed-parse.js";
import {
  currentEventsRoute,
  currentEventsView,
  EVENTS_ROUTE_REQUEST_EVENT,
  publishEventsLastUpdated,
  publishEventsStatus,
  publishEventsView,
} from "./events-state.js";
import { fetchPublicJson, publicApiBase } from "./public-api.js";

const EMPTY_HUB = { feedClub: [], feedEvents: [], hasMainContent: false, live: [], upcoming: [] };
const EVENTS_PAGE_LIMIT = 2000;
const GUEST_REG_KEY = "gvdg_guest_regs";
const REFRESH_MS = 60 * 1000;
const LIVE_REFRESH_MS = 5 * 1000;
const REGISTRATION_REFRESH_EVENT = "gvdg:events-registration-refresh";
const ROUTE_REFRESH_EVENT = "gvdg:events-route-refresh";
const RYDER_CUP_LEAGUE_ID = "4";

const state = {
  courseData: null,
  hub: EMPTY_HUB,
  installed: false,
  loading: false,
  previousResults: [],
  refreshTimer: null,
  liveTimer: null,
};

const hubSubscribers = new Set();
const previousResultsSubscribers = new Set();

function subscribeHub(callback) {
  hubSubscribers.add(callback);
  return () => hubSubscribers.delete(callback);
}

function subscribePreviousResults(callback) {
  previousResultsSubscribers.add(callback);
  return () => previousResultsSubscribers.delete(callback);
}

function notifyHub() {
  hubSubscribers.forEach((callback) => callback(state.hub));
}

function notifyPreviousResults() {
  previousResultsSubscribers.forEach((callback) => callback(state.previousResults));
}

function sanitizeUrl(raw) {
  if (!raw) return "";
  try {
    const url = new URL(String(raw), window.location.href);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

function isRyderCupName(value) {
  return /\bryder\s*cup\b/i.test(String(value || ""));
}

function leagueHash(id) {
  return `#league/${encodeURIComponent(String(id))}`;
}

function ryderCupLeagueHash() {
  return leagueHash(RYDER_CUP_LEAGUE_ID);
}

function stripFeedDecor(value) {
  return String(value || "").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}

function namesKey(value) {
  return stripFeedDecor(value).toLowerCase();
}

function ryderCupFeedHash(item) {
  return isRyderCupName(item?.name) ? ryderCupLeagueHash() : "";
}

function ryderCupEventHash(event) {
  if (!event) return "";
  const leagueId = event.league_id != null ? String(event.league_id) : "";
  if (leagueId && leagueId === RYDER_CUP_LEAGUE_ID) return leagueHash(leagueId);
  return isRyderCupName(event.name) ? ryderCupLeagueHash() : "";
}

function feedDateText(item) {
  const raw = item?.date;
  if (raw) {
    const text = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(text) || text.includes("T")) return formatEventDate(text);
    return stripFeedDecor(text) || "TBD";
  }
  const info = feedDateInfo(item);
  if (!info.isTBD && info.dateObj && !Number.isNaN(info.dateObj.getTime())) {
    return info.dateObj.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  }
  return "TBD";
}

function isPastFeedItem(item) {
  const info = feedDateInfo(item);
  return !info.isTBD && info.isPast;
}

function eventDateForSort(event) {
  return parseEventDate((event && (event.date || event.starts_at)) || null);
}

function isArchivedClubEvent(raw) {
  return isPastClubCalendarEvent(normalizeEvent(raw));
}

function splitFeedByDate(items) {
  const active = [];
  const archived = [];
  for (const item of items || []) {
    (isPastFeedItem(item) ? archived : active).push(item);
  }
  const byDate = (a, b) => feedDateInfo(a).dateObj - feedDateInfo(b).dateObj;
  active.sort(byDate);
  archived.sort((a, b) => feedDateInfo(b).dateObj - feedDateInfo(a).dateObj);
  return { active, archived };
}

function splitClubEventsByDate(items) {
  const active = [];
  const archived = [];
  for (const raw of items || []) {
    const event = normalizeEvent(raw);
    (isArchivedClubEvent(event) ? archived : active).push(event);
  }
  return { active, archived };
}

function feedResultDateText(item) {
  return feedDateText(item) === "TBD" ? "Date TBD" : feedDateText(item);
}

function previousResultFromFeed(item, category) {
  const info = feedDateInfo(item);
  const leagueTarget = ryderCupFeedHash(item);
  const externalHref = leagueTarget ? "" : sanitizeUrl(item?.url);
  return {
    cta: leagueTarget ? "League / Results" : (externalHref ? "Results / Details" : ""),
    dateObj: info.dateObj,
    dateText: feedResultDateText(item),
    detail: stripFeedDecor(item?.detail || ""),
    external: Boolean(externalHref),
    href: leagueTarget || externalHref,
    label: category,
    name: stripFeedDecor(item?.name) || "Previous result",
  };
}

function previousResultFromEvent(raw, courseIndex) {
  const event = normalizeEvent(raw);
  const leagueTarget = ryderCupEventHash(event);
  const courseName = eventCourseSummary(courseIndex, event);
  return {
    cta: leagueTarget ? "League / Results" : "View results",
    dateObj: eventDateForSort(event),
    dateText: formatEventDate(event.date || event.starts_at),
    detail: [courseName, statusLabel(event.status)].filter(Boolean).join(" · "),
    external: false,
    href: leagueTarget || `#event/${encodeURIComponent(event.id)}`,
    label: typeLabel(event.type),
    name: event.name,
    status: event.status,
    statusLabel: statusLabel(event.status),
    typeClass: event.type,
  };
}

function previousResultTime(item) {
  return item?.dateObj && !Number.isNaN(item.dateObj.getTime()) ? item.dateObj.getTime() : 0;
}

function eventHubItem(raw, courseIndex) {
  const event = normalizeEvent(raw);
  return {
    courseName: eventCourseSummary(courseIndex, event),
    dateText: formatEventDate(event.date || event.starts_at),
    href: `#event/${encodeURIComponent(event.id)}`,
    id: event.id,
    name: event.name,
    starts_at: event.starts_at,
    registration_deadline: event.registration_deadline,
    checkin_deadline: event.checkin_deadline,
    status: event.status,
    statusLabel: statusLabel(event.status),
    typeClass: event.type,
    typeLabel: typeLabel(event.type),
    field: Array.isArray(raw?.field) ? raw.field : [],
  };
}

function findClubEventByName(clubEvents, name) {
  const key = namesKey(name);
  if (!key) return null;
  return (clubEvents || []).find((event) => namesKey(event && event.name) === key) || null;
}

function feedHubItem(item, clubEvents) {
  const leagueTarget = ryderCupFeedHash(item);
  const matched = leagueTarget ? null : findClubEventByName(clubEvents, item?.name);
  const clubHref = matched && matched.id != null && matched.id !== ""
    ? `#event/${encodeURIComponent(matched.id)}`
    : "";
  const externalHref = leagueTarget || clubHref ? "" : sanitizeUrl(item?.url);
  const past = Boolean(matched && (matched.status === "final" || matched.status === "cancelled"));
  return {
    cta: leagueTarget ? "League / Results" : clubHref ? (past ? "View results" : "Event details") : (externalHref ? "Register / Details" : ""),
    dateText: feedDateText(item),
    detail: stripFeedDecor(item?.detail || ""),
    external: Boolean(externalHref),
    href: leagueTarget || clubHref || externalHref,
    name: stripFeedDecor(item?.name) || "Event",
  };
}

function filterDuplicateFeed(feedItems, clubEvents) {
  const clubNames = new Set((clubEvents || []).map((event) => namesKey(event && event.name)));
  return (feedItems || []).filter((item) => {
    const key = namesKey(item && item.name);
    return !key || !clubNames.has(key);
  });
}

async function loadCourseData(api) {
  if (!state.courseData || state.courseData.api !== api) {
    state.courseData = {
      api,
      promise: fetchPublicJson(api, "/courses")
        .then((data) => {
          const courses = Array.isArray(data?.courses) ? data.courses : [];
          return { courses, index: buildCourseIndex(courses) };
        })
        .catch(() => ({ courses: [], index: new Map() })),
    };
  }
  return state.courseData.promise;
}

function attachOpenFields(events, openEvents) {
  const byId = new Map();
  for (const event of Array.isArray(openEvents) ? openEvents : []) {
    if (event && event.id != null) byId.set(String(event.id), Array.isArray(event.field) ? event.field : []);
  }
  return (Array.isArray(events) ? events : []).map((event) => {
    if (!event || event.id == null) return event;
    const field = byId.get(String(event.id));
    return field ? { ...event, field } : event;
  });
}

function publishLoadedHub(feed, events, courseIndex) {
  const { active: feedEvents, archived: feedArchived } = splitFeedByDate(feed?.events || []);
  const { active: feedClub, archived: feedClubArchived } = splitFeedByDate(feed?.clubEvents || []);
  const { active: activeEvents, archived: archivedEvents } = splitClubEventsByDate(events || []);
  const { live, upcoming } = bucketEvents(activeEvents);
  const previousLive = new Map((state.hub.live || []).map((event) => [String(event.id), event.liveSnapshot]));
  const previousResults = [
    ...archivedEvents.map((event) => previousResultFromEvent(event, courseIndex)),
    ...feedArchived.map((item) => previousResultFromFeed(item, "Event")),
    ...feedClubArchived.map((item) => previousResultFromFeed(item, "Club Event")),
  ].sort((a, b) => previousResultTime(b) - previousResultTime(a));

  state.hub = {
    feedClub: feedClub.map((item) => feedHubItem(item, events || [])),
    feedEvents: filterDuplicateFeed(feedEvents, [...live, ...upcoming]).map((item) => feedHubItem(item, events || [])),
    hasMainContent: Boolean(feedEvents.length || live.length || upcoming.length),
    live: live.map((event) => {
      const item = eventHubItem(event, courseIndex);
      const snapshot = previousLive.get(String(item.id));
      return snapshot ? { ...item, liveSnapshot: snapshot } : item;
    }),
    upcoming: upcoming.map((event) => eventHubItem(event, courseIndex)),
  };
  state.previousResults = previousResults;
  notifyHub();
  notifyPreviousResults();
  publishEventsView("hub");
  publishEventsLastUpdated(new Date());
  window.dispatchEvent(new CustomEvent(REGISTRATION_REFRESH_EVENT));
  void refreshLiveSnapshots();
}

async function loadHub({ quiet = false } = {}) {
  if (state.loading) return;
  state.loading = true;
  const api = publicApiBase();
  if (!quiet) {
    publishEventsView("status");
    publishEventsStatus({ message: "Loading...", retry: null, tone: "loading" });
  }
  try {
    const courseData = await loadCourseData(api);
    const [feed, eventsData, openData] = await Promise.all([
      fetchPublicJson(api, "/club-feed").catch(() => ({ events: [], clubEvents: [] })),
      fetchPublicJson(api, `/events?limit=${EVENTS_PAGE_LIMIT}&offset=0`).catch(() => ({ events: [] })),
      fetchPublicJson(api, "/registration/open").catch(() => ({ events: [] })),
    ]);
    publishLoadedHub(feed, attachOpenFields(eventsData?.events || [], openData?.events || []), courseData.index);
  } catch {
    if (!quiet) {
      publishEventsLastUpdated(null);
      publishEventsStatus({
        message: "We couldn't load the calendar right now. Please try again.",
        retry: () => loadHub(),
        tone: "error",
      });
      publishEventsView("status");
    }
  } finally {
    state.loading = false;
  }
}

function startRefresh() {
  stopRefresh();
  state.refreshTimer = window.setInterval(() => {
    if (currentEventsView() === "hub") loadHub({ quiet: true });
  }, REFRESH_MS);
  state.liveTimer = window.setInterval(() => {
    if (currentEventsView() === "hub") void refreshLiveSnapshots();
  }, LIVE_REFRESH_MS);
}

function stopRefresh() {
  if (state.refreshTimer) {
    window.clearInterval(state.refreshTimer);
    state.refreshTimer = null;
  }
  if (state.liveTimer) {
    window.clearInterval(state.liveTimer);
    state.liveTimer = null;
  }
}

async function refreshLiveSnapshots() {
  const live = Array.isArray(state.hub.live) ? state.hub.live : [];
  if (!live.length) return;
  const api = publicApiBase();
  const rows = await Promise.all(live.map(async (event) => {
    if (!event || event.id == null) return [String(event && event.id || ""), null];
    try {
      return [String(event.id), await fetchPublicJson(api, `/events/${encodeURIComponent(event.id)}/live`)];
    } catch {
      return [String(event.id), null];
    }
  }));
  const byId = new Map(rows.filter((row) => row[1]));
  if (!byId.size) return;
  state.hub = {
    ...state.hub,
    live: state.hub.live.map((event) => {
      const snapshot = byId.get(String(event && event.id));
      return snapshot ? { ...event, liveSnapshot: snapshot } : event;
    }),
  };
  notifyHub();
}

function guestRegs() {
  try {
    const parsed = JSON.parse(localStorage.getItem(GUEST_REG_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function setGuestReg(eventId, value) {
  const all = guestRegs();
  if (value) all[eventId] = value;
  else delete all[eventId];
  try {
    localStorage.setItem(GUEST_REG_KEY, JSON.stringify(all));
  } catch {
  }
}

async function applyManage(eventId, token) {
  const api = publicApiBase();
  try {
    const data = await fetchPublicJson(api, `/events/${encodeURIComponent(eventId)}/registration?gt=${encodeURIComponent(token)}`);
    if (data?.registration) setGuestReg(eventId, { guestToken: token, name: data.registration.name });
  } catch {
  }
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
  window.dispatchEvent(new CustomEvent(REGISTRATION_REFRESH_EVENT));
  window.dispatchEvent(new CustomEvent(ROUTE_REFRESH_EVENT));
}

function handleRouteRequest(event) {
  stopRefresh();
  const route = event?.detail?.route || currentEventsRoute();
  if (route?.view === "event" || route?.view === "league") return;
  if (route?.view === "manage") {
    applyManage(route.id, route.token);
    return;
  }
  loadHub();
  startRefresh();
}

export function installEventsHubController() {
  if (state.installed) return;
  state.installed = true;
  window.addEventListener(EVENTS_ROUTE_REQUEST_EVENT, handleRouteRequest);
  if (currentEventsRoute()) handleRouteRequest({ detail: { route: currentEventsRoute() } });
}

export function useEventsHub() {
  const [hub, setHub] = React.useState(state.hub);
  React.useEffect(() => subscribeHub(setHub), []);
  return hub;
}

export function useEventsPreviousResults() {
  const [results, setResults] = React.useState(state.previousResults);
  React.useEffect(() => subscribePreviousResults(setResults), []);
  return results;
}
